/**
 * gclidPersist.js
 *
 * Captures the Google Click ID (gclid) from the landing page URL and persists it
 * in sessionStorage so it survives SPA navigation through the checkout funnel.
 *
 * WHY THIS IS NEEDED:
 * When a user clicks a Google Ad, they land on the site with ?gclid=xxx in the URL.
 * As they navigate through the React SPA (date picker → config → checkout → /thankyou),
 * the URL changes and the gclid is lost from the address bar.
 * GTM's auto-tagging sets the _gcl_aw cookie (which handles Ads attribution automatically),
 * but this utility provides a JS-accessible fallback for dataLayer enrichment.
 *
 * USAGE: Call captureGclid() once on app mount (in App.js or index.js).
 * Use getStoredGclid() anywhere to retrieve it.
 */

const GCLID_KEY = '_btc_gclid';
const GCLID_EXPIRY_KEY = '_btc_gclid_expiry';
const GCLID_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days (matches Google Ads default window)

/**
 * Captures gclid (and wbraid/gbraid for iOS) from the current URL and persists to sessionStorage.
 * Also captures utm_source/utm_medium as contextual data.
 * Call this on every page load — it's idempotent and won't overwrite valid stored values.
 */
export function captureGclid() {
  try {
    const params = new URLSearchParams(window.location.search);
    const gclid = params.get('gclid');
    const wbraid = params.get('wbraid'); // iOS Safari fallback
    const gbraid = params.get('gbraid'); // App campaigns fallback

    const clickId = gclid || wbraid || gbraid;
    const clickIdType = gclid ? 'gclid' : wbraid ? 'wbraid' : gbraid ? 'gbraid' : null;

    if (clickId && clickIdType) {
      const expiry = Date.now() + GCLID_TTL_MS;
      sessionStorage.setItem(GCLID_KEY, JSON.stringify({
        value: clickId,
        type: clickIdType,
        captured_at: new Date().toISOString(),
        landing_page: window.location.pathname + window.location.search,
      }));
      sessionStorage.setItem(GCLID_EXPIRY_KEY, String(expiry));
      console.log(`[BTC] Click ID captured: ${clickIdType}=${clickId}`);
    }

    // Also capture UTM params for enhanced attribution context
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');

    if (utmSource) {
      sessionStorage.setItem('_btc_utm', JSON.stringify({
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
      }));
    }
  } catch (e) {
    // sessionStorage may be unavailable (private mode / storage full)
    console.warn('[BTC] Could not capture gclid:', e);
  }
}

/**
 * Returns the stored gclid data object, or null if expired/not found.
 * @returns {{ value: string, type: string, captured_at: string, landing_page: string } | null}
 */
export function getStoredGclid() {
  try {
    const expiry = sessionStorage.getItem(GCLID_EXPIRY_KEY);
    if (expiry && Date.now() > Number(expiry)) {
      sessionStorage.removeItem(GCLID_KEY);
      sessionStorage.removeItem(GCLID_EXPIRY_KEY);
      return null;
    }
    const raw = sessionStorage.getItem(GCLID_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Returns stored UTM data, or null.
 */
export function getStoredUtm() {
  try {
    const raw = sessionStorage.getItem('_btc_utm');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}
